import { z } from "zod";

// ============================================================================
// Auth Schemas
// ============================================================================

export const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters"),
});

export const registerSchema = z
  .object({
    firstName: z
      .string()
      .min(1, "First name is required")
      .min(2, "First name must be at least 2 characters"),
    lastName: z
      .string()
      .min(1, "Last name is required")
      .min(2, "Last name must be at least 2 characters"),
    email: z
      .string()
      .min(1, "Email is required")
      .email("Invalid email address"),
    password: z
      .string()
      .min(1, "Password is required")
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        "Password must contain uppercase, lowercase, number and special character"
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        "Password must contain uppercase, lowercase, number and special character"
      ),
    confirmNewPassword: z.string().min(1, "Please confirm your new password"),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords don't match",
    path: ["confirmNewPassword"],
  });

// ============================================================================
// Panel Schemas
// ============================================================================

export const panelDetailsSchema = z.object({
  title: z
    .string()
    .min(1, "Panel title is required")
    .min(5, "Panel title must be at least 5 characters")
    .max(200, "Panel title must not exceed 200 characters"),
  visibility: z.enum(["PUBLIC", "PRIVATE"]).optional().default("PUBLIC"),
  deadline: z
    .string()
    .min(1, "Deadline is required")
    .refine((date) => new Date(date) > new Date(), {
      message: "Deadline must be in the future",
    }),
  isPublic: z.boolean().optional().default(true),
});

export const panelCreationSchema = panelDetailsSchema;

export const panelUpdateSchema = panelDetailsSchema.partial();

export const panelFilterSchema = z.object({
  status: z.enum(["DRAFT", "IN_PROGRESS", "COMPLETED", "ARCHIVED"]).optional(),
  visibility: z.enum(["PUBLIC", "PRIVATE"]).optional(),
  keyword: z.string().optional(),
});

// ============================================================================
// Cadre Application Form Schema (New Structure)
// ============================================================================

export const conflictOfInterestSchema = z
  .object({
    employeeOfFemaOrDhs: z
      .string({ required_error: "Please select Yes or No" })
      .refine((v) => v === "YES" || v === "NO", {
        message: "Please select Yes or No",
      }),
    retiredEmployeeOfFemaOrDhs: z
      .string({ required_error: "Please select Yes or No" })
      .refine((v) => v === "YES" || v === "NO", {
        message: "Please select Yes or No",
      }),
    currentEmployeeOrContractorOfFEMA_PTS: z
      .string({ required_error: "Please select Yes or No" })
      .refine((v) => v === "YES" || v === "NO", {
        message: "Please select Yes or No",
      }),
    ptsJointVenture: z.string().optional(),
    workLevel: z.string().optional(),
    regions: z.array(z.string()).optional().default([]),
    states: z.array(z.string()).optional().default([]),
    localCommunityName: z.string().optional(),
    pastEmployeeOrContractor: z
      .string({ required_error: "Please select Yes or No" })
      .refine((v) => v === "YES" || v === "NO", {
        message: "Please select Yes or No",
      }),
    nfipInvolvement: z
      .string({ required_error: "Please select Yes or No" })
      .refine((v) => v === "YES" || v === "NO", {
        message: "Please select Yes or No",
      }),
    nfipWorkLevel: z.string().optional(),
    nfipDetails: z.string().optional(),
  })
  .refine(
    (data) => {
      // If current employee is YES, then ptsJointVenture and workLevel are required
      if (data.currentEmployeeOrContractorOfFEMA_PTS === "YES") {
        return data.ptsJointVenture && data.workLevel;
      }
      return true;
    },
    {
      message:
        "PTS joint venture and work level are required when current employee is YES",
      path: ["ptsJointVenture"],
    }
  )
  .refine(
    (data) => {
      // If work level is Regional, then regions array should not be empty
      if (data.workLevel === "Regional") {
        return data.regions && data.regions.length > 0;
      }
      return true;
    },
    {
      message: "At least one region must be selected for Regional work level",
      path: ["regions"],
    }
  )
  .refine(
    (data) => {
      // If work level is Regional, State, or Local, then states array should not be empty
      if (["Regional", "State", "Local"].includes(data.workLevel)) {
        return data.states && data.states.length > 0;
      }
      return true;
    },
    {
      message:
        "At least one state must be selected for Regional/State/Local work level",
      path: ["states"],
    }
  )
  .refine(
    (data) => {
      // If work level is Local, then localCommunityName is required
      if (data.workLevel === "Local") {
        return (
          data.localCommunityName && data.localCommunityName.trim().length > 0
        );
      }
      return true;
    },
    {
      message: "Community name is required for Local work level",
      path: ["localCommunityName"],
    }
  )
  .refine(
    (data) => {
      // If NFIP involvement is YES, then work level is required
      if (data.nfipInvolvement === "YES") {
        return data.nfipWorkLevel && data.nfipWorkLevel.trim().length > 0;
      }
      return true;
    },
    {
      message: "NFIP work level is required when NFIP involvement is YES",
      path: ["nfipWorkLevel"],
    }
  )
  .refine(
    (data) => {
      // If NFIP involvement is YES, then details are required
      if (data.nfipInvolvement === "YES") {
        return data.nfipDetails && data.nfipDetails.trim().length > 0;
      }
      return true;
    },
    {
      message: "NFIP details are required when NFIP involvement is YES",
      path: ["nfipDetails"],
    }
  );

// Panel-specific Conflict of Interest schema
export const panelConflictOfInterestSchema = z.object({
  conflictOfInterest: conflictOfInterestSchema,
});

export const newCadreApplicationSchema = z.object({
  applicantName: z
    .string()
    .min(1, "Full name is required")
    .min(2, "Full name must be at least 2 characters")
    .max(100, "Full name must not exceed 100 characters"),
  email: z
    .string()
    .min(1, "Email address is required")
    .email("Invalid email address"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^[+]?[1-9][\d]{0,15}$/, "Invalid phone number format"),
  education: z.string().min(1, "Education level is required"),
  shortBio: z
    .string()
    .min(1, "Short bio is required")
    .min(20, "Short bio must be at least 20 characters")
    .max(1000, "Short bio must not exceed 1000 characters"),
  expertise: z
    .array(z.string())
    .min(1, "Please select at least one area of expertise"),
  yearsExperience: z.union([z.string(), z.number()]).refine(
    (val) => {
      const num = typeof val === "string" ? parseInt(val) : val;
      return !isNaN(num) && num >= 10;
    },
    {
      message: "Minimum 10 years of experience required",
    }
  ),
  certifications: z.string().optional(),
  resume: z
    .any()
    .refine((file) => file && file instanceof File, {
      message: "Resume upload is required",
    })
    .refine(
      (file) => {
        if (!file) return false;
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        return allowedTypes.includes(file.type);
      },
      {
        message: "Please upload a PDF or Word document",
      }
    )
    .refine(
      (file) => {
        if (!file) return false;
        const maxSize = 5 * 1024 * 1024; // 5MB
        return file.size <= maxSize;
      },
      {
        message: "File size must be less than 5MB",
      }
    ),
  coverLetter: z.string().optional(),
  notes: z.string().optional(),
  conflictOfInterest: conflictOfInterestSchema,
});

export const cadreApplicationSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\+?1?\d{10,14}$/, "Invalid phone number format"),
  organization: z.string().min(1, "Organization is required"),
  position: z.string().min(1, "Position/Title is required"),
  yearsExperience: z
    .number()
    .min(0, "Years of experience cannot be negative")
    .max(70, "Invalid years of experience")
    .or(z.string().min(1, "Years of experience is required").transform(Number)),
  expertise: z
    .array(z.string())
    .min(1, "Please select at least one area of expertise"),
  region: z.string().min(1, "Region is required"),
  availability: z
    .enum(["Available", "Limited", "Not Available"])
    .default("Available"),
  cv: z.any().optional(),
  coverLetter: z
    .string()
    .min(50, "Cover letter must be at least 50 characters")
    .max(2000, "Cover letter must not exceed 2000 characters")
    .optional(),
});

export const cadreProfileUpdateSchema = cadreApplicationSchema.partial();

export const cadreFilterSchema = z.object({
  expertise: z.array(z.string()).optional().default([]),
  yearsExperience: z.string().optional(),
  region: z.string().optional(),
  availability: z.enum(["Available", "Limited", "Not Available"]).optional(),
  rating: z.number().min(0).max(5).optional(),
});

// ============================================================================
// Document Schemas
// ============================================================================

export const documentUploadSchema = z.object({
  title: z
    .string()
    .min(1, "Document title is required")
    .max(200, "Title must not exceed 200 characters"),
  description: z
    .string()
    .max(1000, "Description must not exceed 1000 characters")
    .optional(),
  category: z.string().min(1, "Category is required"),
  file: z
    .any()
    .refine((file) => file && file.size > 0, "File is required")
    .refine(
      (file) => file && file.size <= 10 * 1024 * 1024,
      "File size must not exceed 10MB"
    ),
});

export const documentFilterSchema = z.object({
  category: z.string().optional(),
  uploadedBy: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

// ============================================================================
// User Schemas
// ============================================================================

export const userProfileSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  phone: z
    .string()
    .regex(/^\+?1?\d{10,14}$/, "Invalid phone number format")
    .optional(),
  organization: z.string().optional(),
});

export const userUpdateSchema = userProfileSchema.partial();

// ============================================================================
// Review/Decision Schemas
// ============================================================================

export const shortlistReviewSchema = z.object({
  panelId: z.string().min(1, "Panel ID is required"),
  selectedCadreIds: z
    .array(z.string())
    .min(1, "Please select at least one cadre member"),
  comments: z
    .string()
    .max(1000, "Comments must not exceed 1000 characters")
    .optional(),
  approved: z.boolean().default(false),
});

export const decisionSchema = z.object({
  panelId: z.string().min(1, "Panel ID is required"),
  decision: z
    .enum(["APPROVED", "REJECTED", "NEEDS_REVISION"])
    .default("APPROVED"),
  comments: z
    .string()
    .min(10, "Comments must be at least 10 characters")
    .max(2000, "Comments must not exceed 2000 characters")
    .optional(),
  reviewedBy: z.string().min(1, "Reviewer ID is required"),
});

// ============================================================================
// Contact/Feedback Schemas
// ============================================================================

export const contactSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .min(2, "Name must be at least 2 characters"),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  subject: z
    .string()
    .min(1, "Subject is required")
    .min(5, "Subject must be at least 5 characters")
    .max(200, "Subject must not exceed 200 characters"),
  message: z
    .string()
    .min(1, "Message is required")
    .min(20, "Message must be at least 20 characters")
    .max(2000, "Message must not exceed 2000 characters"),
});

// ============================================================================
// Export all schemas
// ============================================================================

const schemas = {
  // Auth
  loginSchema,
  registerSchema,
  changePasswordSchema,

  // Panel
  panelDetailsSchema,
  panelCreationSchema,
  panelUpdateSchema,
  panelFilterSchema,

  // Cadre
  cadreApplicationSchema,
  newCadreApplicationSchema,
  conflictOfInterestSchema,
  cadreProfileUpdateSchema,
  cadreFilterSchema,

  // Document
  documentUploadSchema,
  documentFilterSchema,

  // User
  userProfileSchema,
  userUpdateSchema,

  // Review
  shortlistReviewSchema,
  decisionSchema,

  // Contact
  contactSchema,
};

export default schemas;
